"use client";

import React, { useState } from 'react';
import { Star } from 'lucide-react';
import { toast } from 'sonner';
import axiosInstance, { endpoints } from '@/lib/axios';

interface SendAvisModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Formation name shown in the subtitle. */
    programName?: string;
    /** Slug of the formation this review is attached to (required to submit). */
    formationSlug?: string;
}

// ---------------------------------------------------------------------------
// Star rating input
// ---------------------------------------------------------------------------

const StarIcon: React.FC<{ filled: boolean }> = ({ filled }) => (
    <svg
        width="19.89"
        height="19.89"
        viewBox="0 0 19 18"
        fill="none"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M8.38586 0.691212C8.68521 -0.230099 9.98862 -0.2301 10.288 0.691211L11.8066 5.36497C11.9404 5.777 12.3244 6.05596 12.7576 6.05596H17.6719C18.6406 6.05596 19.0434 7.29557 18.2597 7.86497L14.284 10.7535C13.9335 11.0082 13.7868 11.4595 13.9207 11.8716L15.4393 16.5453C15.7386 17.4666 14.6842 18.2327 13.9004 17.6633L9.9247 14.7748C9.57421 14.5202 9.09962 14.5202 8.74913 14.7748L4.77339 17.6633C3.98968 18.2327 2.9352 17.4666 3.23455 16.5453L4.75314 11.8716C4.88702 11.4595 4.74036 11.0082 4.38987 10.7535L0.414135 7.86497C-0.369579 7.29557 0.0331967 6.05596 1.00192 6.05596H5.9162C6.34943 6.05596 6.73339 5.777 6.86726 5.36497L8.38586 0.691212Z"
            fill={filled ? "#FFBC42" : "none"}
            stroke="#FFBC42"
            strokeWidth={filled ? 0 : 1.2}
        />
    </svg>
);

interface RatingInputProps {
    value: number;
    onChange: (value: number) => void;
}

const RatingInput: React.FC<RatingInputProps> = ({ value, onChange }) => {
    const [hover, setHover] = useState(0);
    const active = hover || value;

    return (
        <div role="radiogroup" aria-label="Votre note globale" className="flex gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
                <button
                    key={star}
                    type="button"
                    role="radio"
                    aria-checked={value === star}
                    aria-label={`${star} étoile${star > 1 ? "s" : ""}`}
                    onClick={() => onChange(star)}
                    onMouseEnter={() => setHover(star)}
                    onMouseLeave={() => setHover(0)}
                    onFocus={() => setHover(star)}
                    onBlur={() => setHover(0)}
                    className="cursor-pointer rounded p-0.5 transition-transform hover:scale-110 focus-visible:outline-2 focus-visible:outline-[#40BFDE]"
                >
                    <StarIcon filled={star <= active} />
                </button>
            ))}
        </div>
    );
};

// ---------------------------------------------------------------------------
// SendAvisModal
// ---------------------------------------------------------------------------

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "Donnez votre avis" modal.
 *
 * Mirrors {@link DownloadProgramModal}/{@link ShareProgramModal}'s modal shell
 * (overlay, close button, header circle icon, design tokens) and carries the
 * same field set + validation + submit behaviour as {@link SendAvisForm}
 * (rating, title, review, name, email, consent). The parent owns open/close.
 */
const SendAvisModal: React.FC<SendAvisModalProps> = ({
    isOpen,
    onClose,
    programName,
    formationSlug,
}) => {
    const [rating, setRating] = useState(0);
    const [title, setTitle] = useState("");
    const [review, setReview] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [consent, setConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const isValid =
        rating > 0 &&
        title.trim() !== "" &&
        review.trim() !== "" &&
        name.trim() !== "" &&
        EMAIL_PATTERN.test(email.trim()) &&
        consent;

    const resetForm = () => {
        setRating(0);
        setTitle("");
        setReview("");
        setName("");
        setEmail("");
        setConsent(false);
    };

    const onSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!isValid || submitting) return;

        if (!formationSlug) {
            toast.error("Impossible d'envoyer votre avis pour le moment.");
            return;
        }

        setSubmitting(true);
        try {
            // Review is created as "pending" and published once an admin approves it.
            await axiosInstance.post(endpoints.reviews.create(formationSlug), {
                rating,
                title: title.trim(),
                comment: review.trim(),
                reviewer_name: name.trim(),
                reviewer_email: email.trim(),
                consent,
            });
            toast.success(
                "Merci ! Votre avis a bien été envoyé et sera publié après validation.",
            );
            resetForm();
            onClose();
        } catch {
            toast.error(
                "Une erreur est survenue lors de l'envoi. Veuillez réessayer.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const inputClass =
        "w-full h-10 px-3 rounded-lg border border-[#EFEFEF] bg-white placeholder:text-[#ABABAB] text-[12px] font-normal text-[#1B365E] focus:outline-none focus:border-[#40BFDE] focus:ring-1 focus:ring-[#40BFDE]";

    return (
        <div className="fixed inset-0 z-[100050] flex items-center justify-center p-4 bg-[#FFFFFF4D] backdrop-blur-xs">
            <div className="relative w-full max-w-170 bg-white rounded-lg pt-12 pb-6 sm:pb-10 px-5 sm:px-10 border border-[#40BFDE]" style={{ boxShadow: "0px 1px 2px 1px #00000014" }}>
                {/* Close Button */}
                <button
                    onClick={onClose}
                    aria-label="Fermer"
                    className="absolute top-5 right-5 text-[#1B365E] hover:text-[#40BFDE] transition-colors cursor-pointer duration-75"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_send_avis_close)">
                            <path d="M23.2897 4.0987C24.2268 3.16164 24.2268 1.63986 23.2897 0.702796C22.3526 -0.234265 20.8309 -0.234265 19.8938 0.702796L12 8.60409L4.0987 0.710292C3.16164 -0.226769 1.63986 -0.226769 0.702796 0.710292C-0.234265 1.64735 -0.234265 3.16914 0.702796 4.1062L8.60409 12L0.710292 19.9013C-0.226769 20.8384 -0.226769 22.3601 0.710292 23.2972C1.64735 24.2343 3.16914 24.2343 4.1062 23.2972L12 15.3959L19.9013 23.2897C20.8384 24.2268 22.3601 24.2268 23.2972 23.2897C24.2343 22.3526 24.2343 20.8309 23.2972 19.8938L15.3959 12L23.2897 4.0987Z" fill="currentColor" />
                        </g>
                        <defs>
                            <clipPath id="clip0_send_avis_close">
                                <rect width="24" height="24" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>
                </button>

                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-center gap-3 sm:gap-4 mb-4">
                        <div className="w-14 h-14 sm:w-16 sm:h-16 shrink-0 rounded-full bg-[#C7F8FF] flex items-center justify-center">
                            <Star className="w-9.5 h-9.5 sm:w-7 sm:h-7 text-[#40BFDE]" aria-hidden="true" />
                        </div>
                        <h2 className="font-red-hat font-light text-[24px] sm:text-[32px] leading-7 sm:leading-10 text-[#1B365E] tracking-[0.9px]">
                            Donnez votre avis
                        </h2>
                    </div>
                    <p className="text-center font-bold text-[16px] sm:text-[18px] leading-5.5 sm:leading-7 text-[#1B365E] tracking-[0.4px] max-w-md mx-auto">
                        Partagez votre expérience sur {programName ?? 'cette formation'}
                    </p>
                </div>

                {/* Form */}
                <form onSubmit={onSubmit} className="space-y-4 sm:space-y-5 max-w-103 mx-auto">
                    {/* Overall rating */}
                    <div className="flex flex-col gap-2">
                        <span className="text-[12px] font-medium text-[#1B365E]">
                            Votre note globale :
                        </span>
                        <div className="bg-[#F5F5F5] rounded-lg px-2.5 py-3 max-w-42.5">
                            <RatingInput value={rating} onChange={setRating} />
                        </div>
                    </div>

                    {/* Title */}
                    <div>
                        <label htmlFor="avis-modal-title" className="sr-only">
                            Titre de votre avis
                        </label>
                        <input
                            id="avis-modal-title"
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Titre de votre avis"
                            className={inputClass}
                        />
                    </div>

                    {/* Review */}
                    <div>
                        <label htmlFor="avis-modal-review" className="sr-only">
                            Votre avis
                        </label>
                        <textarea
                            id="avis-modal-review"
                            value={review}
                            onChange={(e) => setReview(e.target.value)}
                            placeholder="Votre avis"
                            rows={4}
                            className="w-full px-3 py-2.5 rounded-lg border border-[#EFEFEF] bg-white placeholder:text-[#ABABAB] text-[12px] font-normal text-[#1B365E] focus:outline-none focus:border-[#40BFDE] focus:ring-1 focus:ring-[#40BFDE] resize-none"
                        />
                    </div>

                    {/* Name */}
                    <div>
                        <label htmlFor="avis-modal-name" className="sr-only">
                            Nom
                        </label>
                        <input
                            id="avis-modal-name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Nom"
                            className={inputClass}
                        />
                    </div>

                    {/* Email */}
                    <div>
                        <label htmlFor="avis-modal-email" className="sr-only">
                            Email
                        </label>
                        <input
                            id="avis-modal-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Email"
                            className={inputClass}
                        />
                    </div>

                    {/* Consent */}
                    <label className="flex items-start gap-2.5 text-[12px] text-[#1B365E] font-medium tracking-[0.25px] cursor-pointer">
                        <input
                            type="checkbox"
                            checked={consent}
                            onChange={(e) => setConsent(e.target.checked)}
                            className="mt-0.5 h-4 w-4 shrink-0 accent-[#1B365E] cursor-pointer"
                        />
                        <span>
                            Cette note est fondée sur ma propre expérience et mon véritable avis
                        </span>
                    </label>

                    <button
                        type="submit"
                        className={`w-full h-12 sm:h-14.5 border font-bold text-[16px] rounded-lg transition-all duration-300 ${isValid && !submitting
                            ? "bg-[#1B365E] border-[#1B365E] text-white hover:bg-[#152b4d] cursor-pointer"
                            : "bg-[#D9D9D9] border-[#ABABAB] text-[#9CA3AF] cursor-not-allowed"
                            }`}
                        disabled={!isValid || submitting}
                    >
                        {submitting ? "Envoi..." : "Envoyer mon avis"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default SendAvisModal;
